import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import {
  AssignmentReturn as AssignmentReturnIcon,
  History as HistoryIcon,
  Inventory2 as Inventory2Icon,
  Payments as PaymentsIcon,
  PhotoLibrary as PhotoLibraryIcon,
  ToggleOff,
  ToggleOn,
} from '@mui/icons-material';
import AuditHistoryDrawer from './AuditHistoryDrawer';
import type {
  AdminConsignorOption,
  ProductConditionOption,
  ProductSizeOption,
} from '../types/adminInventoryAdd';
import {
  createProductCondition,
  createProductSize,
  fetchAdminConsignors,
} from '../api/adminInventoryAdd';
import {
  fetchInventoryConditionsByCategory,
  fetchInventorySizesByCategory,
  type InventoryVariantEditData,
  fetchInventoryVariantForEdit,
  runInventoryVariantQuickAction,
  updateInventoryVariant,
} from '../api/inventoryAdmin';
import { computeAutoPublicPrice } from '../utils/consignmentPricing';
import { formatMoneyDisplay, parseMoneyInputToNumber } from '../utils/moneyDisplay';
import InventoryStatusChip from './InventoryStatusChip';
import InventoryPhotosPanel, {
  buildProductPhotoSlots,
  buildUsedVariantPhotoSlots,
} from './InventoryPhotosPanel';

type EditableOption = ProductSizeOption | ProductConditionOption;
type PendingCreateKind = 'size' | 'condition';

interface PendingCreate {
  kind: PendingCreateKind;
  name: string;
}

interface VariantFormState {
  comments: string;
  consignorId: number | null;
  sizeId: number | null;
  conditionId: number | null;
  cost: string;
  publicPrice: string;
  autoPublicPrice: boolean;
}

interface AdminEditInventoryVariantModalProps {
  open: boolean;
  inventoryId: number | null;
  onClose: () => void;
  onSaved: () => void;
}

function renderAutocompleteFieldInput(
  params: AutocompleteRenderInputParams,
  label: string
): React.ReactElement {
  return (
    <TextField
      id={params.id}
      disabled={params.disabled}
      fullWidth={params.fullWidth}
      size={params.size}
      label={label}
      slotProps={{
        inputLabel: params.InputLabelProps,
        htmlInput: params.inputProps,
        input: {
          ref: params.InputProps.ref,
          className: params.InputProps.className,
          startAdornment: params.InputProps.startAdornment,
          endAdornment: params.InputProps.endAdornment,
        },
      }}
    />
  );
}

function toTitleCase(text: string): string {
  return text
    .split(' ')
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ')
    .trim();
}

function AdminEditInventoryVariantModal({
  open,
  inventoryId,
  onClose,
  onSaved,
}: AdminEditInventoryVariantModalProps): React.ReactElement {
  const blackContainedBtnSx = {
    textTransform: 'none',
    fontWeight: 800,
    py: 1.2,
    bgcolor: '#000',
    '&:hover': { bgcolor: '#1e293b' },
  };
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState<null | PendingCreateKind | 'status'>(null);
  const [error, setError] = useState<string | null>(null);
  const [variant, setVariant] = useState<InventoryVariantEditData | null>(null);
  const [consignors, setConsignors] = useState<AdminConsignorOption[]>([]);
  const [sizes, setSizes] = useState<ProductSizeOption[]>([]);
  const [conditions, setConditions] = useState<ProductConditionOption[]>([]);
  const [sizeInput, setSizeInput] = useState('');
  const [conditionInput, setConditionInput] = useState('');
  const [pendingCreate, setPendingCreate] = useState<PendingCreate | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [form, setForm] = useState<VariantFormState>({
    comments: '',
    consignorId: null,
    sizeId: null,
    conditionId: null,
    cost: '',
    publicPrice: '',
    autoPublicPrice: true,
  });
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: 'rgba(248, 250, 252, 1)',
    },
  };

  const loadVariant = useCallback(async () => {
    if (!open || !inventoryId) return;
    setLoading(true);
    setError(null);
    const [variantResult, consignorList] = await Promise.all([
      fetchInventoryVariantForEdit(inventoryId),
      fetchAdminConsignors(),
    ]);
    if (!variantResult.ok) {
      setError(variantResult.error);
      setLoading(false);
      return;
    }
    setVariant(variantResult.data);
    setConsignors(consignorList);
    setForm({
      comments: variantResult.data.comments ?? '',
      consignorId: variantResult.data.consignorId,
      sizeId: variantResult.data.sizeId,
      conditionId: variantResult.data.conditionId,
      cost: formatMoneyDisplay(variantResult.data.cost),
      publicPrice: formatMoneyDisplay(variantResult.data.publicPrice),
      autoPublicPrice: false,
    });
    setSizeInput('');
    setConditionInput('');
    if (variantResult.data.categoryId) {
      const [sizeItems, conditionItems] = await Promise.all([
        fetchInventorySizesByCategory(variantResult.data.categoryId),
        fetchInventoryConditionsByCategory(variantResult.data.categoryId),
      ]);
      setSizes(sizeItems);
      setConditions(conditionItems);
    } else {
      setSizes([]);
      setConditions([]);
    }
    setLoading(false);
  }, [open, inventoryId]);

  useEffect(() => {
    loadVariant().catch(() => {});
  }, [loadVariant]);

  const selectedConsignor = useMemo(
    () => consignors.find((x) => x.id === form.consignorId) ?? null,
    [consignors, form.consignorId]
  );

  useEffect(() => {
    if (!form.autoPublicPrice || !selectedConsignor) return;
    const cost = parseMoneyInputToNumber(form.cost);
    const nextPublic = Number.isFinite(cost)
      ? formatMoneyDisplay(computeAutoPublicPrice(cost, selectedConsignor.commission))
      : '';
    setForm((prev) =>
      prev.publicPrice === nextPublic ? prev : { ...prev, publicPrice: nextPublic }
    );
  }, [form.autoPublicPrice, form.cost, selectedConsignor]);

  const isReadOnly = variant?.status === 'Vendido' || variant?.status === 'Devuelto';
  const canRunQuickActions = variant?.status !== 'Vendido';
  const canOnlyActivate = variant?.status === 'Devuelto';
  const shouldShowActivateAction = variant?.status === 'Inactivo' || variant?.status === 'Devuelto';
  const photosDisabled = isReadOnly || (variant?.isUsedVariant && !variant.clean);
  const photosDisabledMessage = (() => {
    if (isReadOnly) return 'No se pueden modificar fotos en variantes vendidas o devueltas.';
    if (variant?.isUsedVariant && !variant.clean) {
      return 'Marca la variante como limpia antes de subir o eliminar fotos.';
    }
    return undefined;
  })();

  const sizeOptions = (() => {
    const q = sizeInput.trim();
    const base = sizes.filter((x) => (q ? x.label.toLowerCase().includes(q.toLowerCase()) : true));
    if (!q || !variant?.categoryId) return base;
    return [
      ...base,
      {
        id: -1,
        label: '+ Nueva talla',
      },
    ];
  })();

  const conditionOptions = (() => {
    const q = conditionInput.trim();
    const base = conditions.filter((x) =>
      q ? x.label.toLowerCase().includes(q.toLowerCase()) : true
    );
    if (!q || !variant?.categoryId) return base;
    return [
      ...base,
      {
        id: -1,
        label: '+ Nueva condición',
      },
    ];
  })();

  const onConfirmCreate = async () => {
    if (!pendingCreate || !variant?.categoryId) return;
    setActionLoading(pendingCreate.kind);
    if (pendingCreate.kind === 'size') {
      const created = await createProductSize({
        name: pendingCreate.name,
        categoryId: variant.categoryId,
      });
      if (created) {
        setSizes((prev) => (prev.some((x) => x.id === created.id) ? prev : [...prev, created]));
        setForm((prev) => ({ ...prev, sizeId: created.id }));
      }
    } else {
      const created = await createProductCondition({
        name: pendingCreate.name,
        categoryId: variant.categoryId,
      });
      if (created) {
        setConditions((prev) =>
          prev.some((x) => x.id === created.id) ? prev : [...prev, created]
        );
        setForm((prev) => ({ ...prev, conditionId: created.id }));
      }
    }
    setActionLoading(null);
    setPendingCreate(null);
  };

  const onSave = async () => {
    if (!variant || isReadOnly || !inventoryId) return;
    const cost = parseMoneyInputToNumber(form.cost);
    const publicPrice = parseMoneyInputToNumber(form.publicPrice);
    if (
      !form.consignorId ||
      !form.sizeId ||
      !form.conditionId ||
      !Number.isFinite(cost) ||
      !Number.isFinite(publicPrice)
    ) {
      setError('Completa consignador, talla, condición, costo y precio público válidos.');
      return;
    }
    setSaving(true);
    setError(null);
    const result = await updateInventoryVariant(inventoryId, {
      comments: form.comments,
      consignorId: form.consignorId,
      sizeId: form.sizeId,
      conditionId: form.conditionId,
      cost,
      publicPrice,
    });
    setSaving(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    onSaved();
  };

  const onQuickStatus = async (action: 'activate' | 'deactivate' | 'markReturned') => {
    if (!inventoryId || !canRunQuickActions) return;
    setActionLoading('status');
    setError(null);
    const result = await runInventoryVariantQuickAction(inventoryId, action);
    setActionLoading(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    await loadVariant();
  };

  const photoPanel =
    variant && inventoryId ? (
      <InventoryPhotosPanel
        inventoryId={inventoryId}
        scope={variant.isUsedVariant ? 'variant' : 'product'}
        requiredSlots={
          variant.isUsedVariant
            ? buildUsedVariantPhotoSlots(variant.conditionPhotos)
            : buildProductPhotoSlots(variant.productPhotos, variant.thumbnailUrl)
        }
        extraPhotos={variant.extraPhotos}
        legacyPhotos={variant.legacyPhotos}
        disabled={photosDisabled}
        disabledMessage={photosDisabledMessage}
        sharedProductNote={
          !variant.isUsedVariant
            ? 'Estas fotos son del producto compartido y aplican a todas las variantes deadstock.'
            : undefined
        }
        onUpdated={() => {
          loadVariant().catch(() => {});
        }}
      />
    ) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 28px 80px rgba(15, 23, 42, 0.28)',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pr: 7 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
          Editar variante
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ajusta datos operativos de esta pieza en inventario.
        </Typography>
        {variant ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1.25, flexWrap: 'wrap' }}>
            <InventoryStatusChip status={variant.status} />
            <Chip
              size="small"
              label={`SKU: ${variant.sku || 'Sin SKU'}`}
              sx={{ fontWeight: 700 }}
            />
          </Box>
        ) : null}
        <Box sx={{ position: 'absolute', right: 8, top: 8, display: 'flex', gap: 0.5 }}>
          {inventoryId != null && (
            <Tooltip title="Ver historial de cambios">
              <IconButton
                aria-label="Ver historial de la variante"
                onClick={() => setHistoryOpen(true)}
                size="small"
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8fafc' }}>
        {loading || !variant ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1.35fr' }, gap: 2.5 }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <PhotoLibraryIcon sx={{ color: 'text.primary' }} fontSize="small" />
                  {variant.isUsedVariant ? 'Fotos de condición' : 'Fotos del producto'}
                </Typography>
                {photoPanel}
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Acciones rápidas
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {!canRunQuickActions ? (
                    <Alert severity="info">
                      Producto vendido: no hay acciones rápidas disponibles.
                    </Alert>
                  ) : null}
                  {canRunQuickActions && canOnlyActivate ? (
                    <Button
                      startIcon={<ToggleOn />}
                      variant="contained"
                      onClick={() => onQuickStatus('activate')}
                      disabled={actionLoading === 'status'}
                      sx={blackContainedBtnSx}
                    >
                      Marcar como activo
                    </Button>
                  ) : null}
                  {canRunQuickActions && !canOnlyActivate ? (
                    <>
                      <Button
                        startIcon={shouldShowActivateAction ? <ToggleOn /> : <ToggleOff />}
                        variant={shouldShowActivateAction ? 'contained' : 'outlined'}
                        onClick={() =>
                          onQuickStatus(shouldShowActivateAction ? 'activate' : 'deactivate')
                        }
                        disabled={actionLoading === 'status'}
                        sx={
                          shouldShowActivateAction
                            ? blackContainedBtnSx
                            : { textTransform: 'none', fontWeight: 800, py: 1.2 }
                        }
                      >
                        {shouldShowActivateAction ? 'Activar producto' : 'Desactivar producto'}
                      </Button>
                      <Button
                        startIcon={<AssignmentReturnIcon />}
                        variant="outlined"
                        color="warning"
                        onClick={() => onQuickStatus('markReturned')}
                        disabled={actionLoading === 'status'}
                        sx={{ textTransform: 'none', fontWeight: 800, py: 1.2 }}
                      >
                        Marcar como devuelto
                      </Button>
                    </>
                  ) : null}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Inventory2Icon sx={{ color: 'text.primary' }} fontSize="small" />
                  <span>Detalle de variante</span>
                </Box>
              </Typography>

              <Box
                sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    SKU
                  </Typography>
                  <Typography sx={{ fontWeight: 700 }}>{variant.sku || 'Sin SKU'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Estado actual
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <InventoryStatusChip status={variant.status} />
                  </Box>
                </Box>
                <Autocomplete<AdminConsignorOption, false, false, false>
                  options={consignors}
                  value={consignors.find((x) => x.id === form.consignorId) ?? null}
                  onChange={(_, v) => setForm((prev) => ({ ...prev, consignorId: v?.id ?? null }))}
                  getOptionLabel={(o) => o.fullName}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  disabled={isReadOnly}
                  renderInput={(params) => (
                    <Box sx={{ ...fieldSx }}>
                      {renderAutocompleteFieldInput(params, 'Consignador')}
                    </Box>
                  )}
                />

                <Autocomplete<EditableOption, false, false, false>
                  options={sizeOptions}
                  value={sizes.find((x) => x.id === form.sizeId) ?? null}
                  inputValue={sizeInput}
                  onInputChange={(_, v) => setSizeInput(v)}
                  disabled={isReadOnly || !variant.categoryId}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  onChange={(_, v) => {
                    if (!v) {
                      setForm((prev) => ({ ...prev, sizeId: null }));
                      return;
                    }
                    if (v.id === -1) {
                      setPendingCreate({ kind: 'size', name: toTitleCase(sizeInput) });
                      return;
                    }
                    setForm((prev) => ({ ...prev, sizeId: v.id }));
                  }}
                  renderInput={(params) => (
                    <Box sx={{ ...fieldSx }}>{renderAutocompleteFieldInput(params, 'Talla')}</Box>
                  )}
                />

                <Autocomplete<EditableOption, false, false, false>
                  options={conditionOptions}
                  value={conditions.find((x) => x.id === form.conditionId) ?? null}
                  inputValue={conditionInput}
                  onInputChange={(_, v) => setConditionInput(v)}
                  disabled={isReadOnly || !variant.categoryId}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  onChange={(_, v) => {
                    if (!v) {
                      setForm((prev) => ({ ...prev, conditionId: null }));
                      return;
                    }
                    if (v.id === -1) {
                      setPendingCreate({ kind: 'condition', name: toTitleCase(conditionInput) });
                      return;
                    }
                    setForm((prev) => ({ ...prev, conditionId: v.id }));
                  }}
                  renderInput={(params) => (
                    <Box sx={{ ...fieldSx }}>
                      {renderAutocompleteFieldInput(params, 'Condición')}
                    </Box>
                  )}
                />

                <TextField
                  label="Costo (pago consignador)"
                  size="small"
                  value={form.cost}
                  onChange={(e) => setForm((prev) => ({ ...prev, cost: e.target.value }))}
                  onBlur={(e) => {
                    const n = parseMoneyInputToNumber(e.target.value);
                    if (Number.isFinite(n)) {
                      setForm((prev) => ({ ...prev, cost: formatMoneyDisplay(n) }));
                    }
                  }}
                  disabled={isReadOnly}
                  sx={fieldSx}
                />

                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Precio público
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.autoPublicPrice}
                          onChange={(_, checked) =>
                            setForm((prev) => ({ ...prev, autoPublicPrice: checked }))
                          }
                          disabled={isReadOnly}
                        />
                      }
                      label="Auto"
                      sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 11 } }}
                    />
                  </Box>
                  <TextField
                    size="small"
                    fullWidth
                    value={form.publicPrice}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        publicPrice: e.target.value,
                        autoPublicPrice: false,
                      }))
                    }
                    onBlur={(e) => {
                      const n = parseMoneyInputToNumber(e.target.value);
                      if (Number.isFinite(n)) {
                        setForm((prev) => ({ ...prev, publicPrice: formatMoneyDisplay(n) }));
                      }
                    }}
                    disabled={isReadOnly || form.autoPublicPrice}
                    sx={fieldSx}
                  />
                </Box>

                <TextField
                  label="Comentarios"
                  size="small"
                  multiline
                  minRows={3}
                  sx={{ ...fieldSx, gridColumn: { xs: '1 / -1', md: '1 / -1' } }}
                  value={form.comments}
                  onChange={(e) => setForm((prev) => ({ ...prev, comments: e.target.value }))}
                  disabled={isReadOnly}
                />
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(15, 23, 42, 0.06)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentsIcon sx={{ color: 'text.primary' }} fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Margen plataforma
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                  {(() => {
                    const cost = parseMoneyInputToNumber(form.cost);
                    const price = parseMoneyInputToNumber(form.publicPrice);
                    if (!Number.isFinite(cost) || !Number.isFinite(price)) return '--';
                    return formatMoneyDisplay(price - cost);
                  })()}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none', fontWeight: 700 }}>
          Cerrar
        </Button>
        <Button
          onClick={() => {
            onSave().catch(() => {});
          }}
          variant="contained"
          disabled={saving || loading || isReadOnly}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            bgcolor: '#000',
            '&:hover': { bgcolor: '#1e293b' },
          }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Guardar cambios'}
        </Button>
      </DialogActions>

      <AuditHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        mode="variant"
        entityId={inventoryId}
        entityTitle={variant?.title || undefined}
      />

      <Dialog
        open={Boolean(pendingCreate)}
        onClose={() => setPendingCreate(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar creación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Crear {pendingCreate?.kind === 'size' ? 'talla' : 'condición'}{' '}
            <strong>{pendingCreate?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingCreate(null)} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={() => onConfirmCreate().catch(() => {})}
            variant="contained"
            disabled={Boolean(actionLoading)}
            sx={{ bgcolor: '#000', '&:hover': { bgcolor: '#1e293b' } }}
          >
            Crear
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default AdminEditInventoryVariantModal;
